(ns practitest.frontend.device-connection.stream
  "HTTP streaming connection using fetch API with ReadableStream.")

(defn- parse-json [s]
  (try
    (js->clj (js/JSON.parse s) :keywordize-keys true)
    (catch :default _
      nil)))

(defn- process-chunk
  "Process a chunk of text, handling partial lines.
  Returns [complete-messages remaining-buffer]."
  [buffer chunk]
  (let [text (str buffer chunk)
        lines (.split text "\n")
        complete-lines (butlast lines)
        remaining (last lines)]
    [(->> complete-lines
          (filter seq)
          (map parse-json)
          (filter some?))
     (or remaining "")]))

(defn connect!
  "Open a streaming HTTP connection.

  Options:
  - `:base-url`: service base URL (required)
  - `:connect-path`: GET stream path (default \"/stream/1/connect\")
  - `:message-path`: POST message path (default \"/stream/1/message\").
                     `?conn_id=...` is appended automatically
  - `:headers`: extra request headers
  - `:on-message`: callback for each message (required)
  - `:on-open`: callback when connected
  - `:on-close`: callback when connection closes (receives {:reason ...})
  - `:on-error`: callback on error (receives error)

  Returns an atom (the client) - pass it to `send!`, `disconnect!`,
  `connected?`, `conn-id`."
  [{:keys [base-url connect-path message-path headers
           on-message on-open on-close on-error]
    :or {connect-path "/stream/1/connect"
         message-path "/stream/1/message"}}]
  (let [abort-controller (js/AbortController.)
        conn (atom {:state :connecting
                    :base-url base-url
                    :message-path message-path
                    :abort-controller abort-controller
                    :conn-id nil})]
    (-> (js/fetch (str base-url connect-path)
                  #js {:method "GET"
                       :headers (clj->js (merge {"Accept" "text/plain"} headers))
                       :credentials "include"
                       :signal (.-signal abort-controller)})
        (.then (fn [response]
                 (if (.-ok response)
                   (let [reader (.getReader (.-body response))
                         decoder (js/TextDecoder.)]
                     (swap! conn assoc :state :connected)
                     (letfn [(read-loop [buffer]
                               (-> (.read reader)
                                   (.then (fn [result]
                                            (if (.-done result)
                                              (do
                                                (swap! conn assoc :state :closed)
                                                (when on-close (on-close {:reason "stream-ended"})))
                                              (let [chunk (.decode decoder (.-value result))
                                                    [messages remaining] (process-chunk buffer chunk)]
                                                (doseq [msg messages]
                                                  (when (= (:type msg) "connected")
                                                    (swap! conn assoc :conn-id (:conn_id msg))
                                                    (when on-open (on-open)))
                                                  (on-message msg))
                                                (read-loop remaining)))))
                                   (.catch (fn [err]
                                             (when-not (= (.-name err) "AbortError")
                                               (swap! conn assoc :state :error)
                                               (when on-error (on-error err)))))))]
                       (read-loop "")))
                   (do
                     (swap! conn assoc :state :error)
                     (when on-error
                       (on-error (ex-info "Connection rejected" {:status (.-status response)})))))))
        (.catch (fn [err]
                  (when-not (= (.-name err) "AbortError")
                    (swap! conn assoc :state :error)
                    (when on-error (on-error err))))))
    conn))

(defn disconnect!
  "Disconnect the streaming connection."
  [conn]
  (when-let [controller (:abort-controller @conn)]
    (.abort controller))
  (swap! conn assoc :state :closed))

(defn connected? [conn]
  (= (:state @conn) :connected))

(defn conn-id [conn]
  (:conn-id @conn))

(defn send!
  "Send a message over the stream. Returns a promise."
  [conn message]
  (let [{:keys [base-url message-path conn-id]} @conn]
    (-> (js/fetch (str base-url message-path "?conn_id=" conn-id)
                  #js {:method "POST"
                       :headers #js {"Content-Type" "application/json"}
                       :credentials "include"
                       :body (js/JSON.stringify (clj->js message))})
        (.then (fn [response]
                 (if (.-ok response)
                   (.json response)
                   (throw (ex-info "Send failed" {:status (.-status response)})))))
        (.then (fn [json]
                 (js->clj json :keywordize-keys true))))))
